import React from 'react';
import { flushSync } from 'react-dom';
import { Marker, Popup } from 'react-leaflet';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import MarkerClusterGroup from '@changey/react-leaflet-markercluster';
import { Button, RadioButton, Select, type SelectOption, Switch, Disclosure, Icon, ArrowToggle, TextInput } from '@gravity-ui/uikit';
import { MapPin, ListUl } from '@gravity-ui/icons';

import Publication from '@components/Publication/Publication.loadable';
import { EmptyContent } from '@components/EmptyContent';
import { PublicationMini } from '@components/PublicationMini';
import { LayoutPage } from '@components/LayoutPage';
import { PublicationSkeleton } from '@components/Publication/Publication.skeleton';
import { publicationCn } from '@components/Publication/Publication.const';
import { Card } from '@components/Card';
import { Map } from '@components/Map';
import { VisibilityObserver } from '@components/VisibilityObserver';
import type { PublicationType } from '@typings/api/IPublication';
import { i18n } from '@client/const';
import { useShareLink } from '@lib/useShareLink';
import { useClientContext } from '@client/lib/ClientContext';
import { withBackButton } from '@client/lib/withBackButton';
import { useCountryList } from '@client/lib/useCountryList';
import { LeafletBlueMarker } from '@client/lib/leaflet/getMarkerPin';
import { usePublicationOptionNames } from '@lib/usePublicationOptionNames';
import { objectKeys } from '@lib/objectKeys';

import { indexControlsCn, indexLinkCn, indexMapCn, indexSearchButtonContainer, indexSearchCn, indexSearchFormCn, indexSearchInputCn, indexSearchMiniInputCn, indexSearchSpoilerCn, indexSearchSwitchCn } from './Index.const';
import { i18n as i18nIndex } from './Index.i18n';
import { usePublications } from './Index.utils/usePublications';
import { IndexNeedAuthAlert } from './-NeedAuthAlert/Index-NeedAuthAlert';

import './Index.scss';

const viewOptions: SelectOption[] = [
    { value: 'map', content: <Icon data={MapPin} size={18} /> },
    { value: 'list', content: <Icon data={ListUl} size={18} /> },
];

const defaultMapCenter = { lat: 41.5, lng: 41.5 };

export const Index: React.FC = withBackButton(() => {
    const {
        publications,
        loading,
        request,
        loadNext,
        countries,
        cities,
        filters,
        setFilter,
        resetFilters,
        isAllLoaded,
    } = usePublications();
    const countryList = useCountryList(countries);
    const [params] = useSearchParams();
    const navigate = useNavigate();
    const optionNames = usePublicationOptionNames();

    const { share, progress: shareProgress } = useShareLink({
        type: 'search',
        filters,
    });

    const setTypePublication = React.useCallback((type: string) => setFilter('typePublication', type as PublicationType), [setFilter]);
    const setCity = React.useCallback((cities: string[]) => setFilter('city', Number(cities[0])), [setFilter]);
    const setCountry = React.useCallback((countries: string[]) => setFilter('country', Number(countries[0])), []);
    const setTypeApartment = React.useCallback((types: string[]) => setFilter('typeApartment', Number(types[0])), [setFilter]);
    const setBathroom = React.useCallback((bathrooms: string[]) => setFilter('bathroom', Number(bathrooms[0])), [setFilter]);
    const setHeating = React.useCallback((heating: boolean) => setFilter('heating', heating), [setFilter]);
    const setOven = React.useCallback((oven: boolean) => setFilter('oven', oven), [setFilter]);
    const setDishwasher = React.useCallback((dishwasher: boolean) => setFilter('dishwasher', dishwasher), [setFilter]);
    const setBalcony = React.useCallback((balcony: boolean) => setFilter('balcony', balcony), [setFilter]);
    const setAnimal = React.useCallback((animal: boolean) => setFilter('animal', animal), [setFilter]);
    const setConditioner = React.useCallback((conditioner: boolean) => setFilter('conditioner', conditioner), [setFilter]);
    const setMicrowave = React.useCallback((microwave: boolean) => setFilter('microwave', microwave), [setFilter]);
    const setPriceFrom = React.useCallback((priceFrom: string) => setFilter('priceFrom', Number(priceFrom)), [setFilter]);
    const setPriceTo = React.useCallback((priceTo: string) => setFilter('priceTo', Number(priceTo)), [setFilter]);
    const setSquareFrom = React.useCallback((squareFrom: string) => setFilter('squareFrom', Number(squareFrom)), [setFilter]);
    const setSquareTo = React.useCallback((squareTo: string) => setFilter('squareTo', Number(squareTo)), [setFilter]);
    const setFloorFrom = React.useCallback((floorFrom: string) => setFilter('floorFrom', Number(floorFrom)), [setFilter]);
    const setFloorTo = React.useCallback((floorTo: string) => setFilter('floorTo', Number(floorTo)), [setFilter]);
    const [{ lang }] = useClientContext();
    const [view, setView] = React.useState<string>('list');

    const mapCenter = React.useMemo(() => {
        const count = publications.length;

        if (publications === undefined || count === 0) {
            return defaultMapCenter;
        }

        const { lat, lon } = publications.reduce((result, { lat, lon }) => {
            result.lat += lat;
            result.lon += lon;

            return result;
        }, { lat: 0, lon: 0 });

        return {
            lat: lat / count,
            lng: lon / count,
        };
    }, [publications]);

    const [expanded, setExpanded] = React.useState<boolean>(false);

    const handleResetFilters = React.useCallback(() => {
        navigate('/');
        resetFilters();
        setExpanded(false);

        flushSync(request);
    }, []);

    const handleSearchForm: React.FormEventHandler<HTMLFormElement> = event => {
        event.preventDefault();

        const params = new URLSearchParams();

        objectKeys(filters).forEach(key => {
            let value = filters[key];

            if (typeof value === 'boolean') {
                value = Number(value);
            }

            if (value) {
                params.set(key, String(value));
            }
        });

        navigate(`./?${params.toString()}`);

        request();
    };

    const citiesOptions = React.useMemo<SelectOption[] | undefined>(() => {
        if (cities === undefined) return undefined;

        return cities.map(city => ({
            value: String(city.id),
            content: city[`name:${lang}`],
        }));
    }, [lang, cities]);

    return (
        <LayoutPage>
            {params.has('need_auth') && <IndexNeedAuthAlert />}

            <Card className={indexSearchCn}>
                <Disclosure
                    expanded={expanded}
                    onUpdate={setExpanded}
                    arrowPosition="end"
                    size="xl"
                    keepMounted={false}
                >
                    <Disclosure.Summary>
                        {(props, defaultButton) => (
                            <div className={indexControlsCn}>
                                <Button
                                    {...defaultButton.props}
                                    size="l"
                                    width="max"
                                    view="raised"
                                    className={indexSearchSpoilerCn}
                                    id="disclosure_button"
                                >
                                    {i18nIndex('search')}
                                    <ArrowToggle
                                        size={25}
                                        direction={props.expanded ? 'top' : 'bottom'}
                                    />
                                </Button>
                                <RadioButton
                                    size="l"
                                    defaultValue={viewOptions[1].value}
                                    value={view}
                                    onUpdate={setView}
                                    options={viewOptions}
                                    name="view_filter"
                                />
                            </div>
                        )}
                    </Disclosure.Summary>
                    <div className={indexSearchFormCn}>
                        <form onSubmit={handleSearchForm}>
                            <RadioButton
                                value={filters.typePublication ?? ''}
                                onUpdate={setTypePublication}
                                name="typePublication"
                                options={optionNames.typePublication}
                                width="max"
                                size="l"
                            />
                            <Select
                                className={indexSearchInputCn}
                                placeholder={i18n('country')}
                                size="l"
                                width="max"
                                name="country"
                                onUpdate={setCountry}
                                options={countryList}
                                value={[filters.country ? String(filters.country) : '']}
                                id="select_country"
                            />
                            <Select
                                className={indexSearchInputCn}
                                placeholder={i18n('city')}
                                size="l"
                                width="max"
                                name="city"
                                disabled={citiesOptions === undefined || !filters.country}
                                loading={citiesOptions === undefined || !filters.country}
                                onUpdate={setCity}
                                options={citiesOptions}
                                value={[filters.city ? String(filters.city) : '']}
                                id="select_city"
                            />
                            <Select
                                className={indexSearchInputCn}
                                placeholder={i18n('typeApartment')}
                                size="l"
                                width="max"
                                name="typeApartment"
                                onUpdate={setTypeApartment}
                                options={optionNames.apartmentType}
                                value={[optionNames.apartmentType[filters.typeApartment || 0].value || '']}
                                id="select_typeApartment"
                            />
                            <div className={indexSearchMiniInputCn}>
                                <TextInput
                                    className={indexSearchInputCn}
                                    name="floor"
                                    onUpdate={setFloorFrom}
                                    placeholder={i18n('floorFrom')}
                                    size="l"
                                    type="number"
                                    controlProps={{
                                        min: 0,
                                    }}
                                    value={String(filters.floorFrom)}
                                />
                                <TextInput
                                    className={indexSearchInputCn}
                                    name="floor"
                                    onUpdate={setFloorTo}
                                    placeholder={i18n('floorTo')}
                                    size="l"
                                    type="number"
                                    controlProps={{
                                        min: 0,
                                    }}
                                    value={String(filters.floorTo)}
                                />
                            </div>
                            <div className={indexSearchMiniInputCn}>
                                <TextInput
                                    className={indexSearchInputCn}
                                    name="squareFrom"
                                    onUpdate={setSquareFrom}
                                    placeholder={i18n('squareFrom')}
                                    size="l"
                                    controlProps={{
                                        min: 0,
                                    }}
                                    type="number"
                                    value={String(filters.squareFrom)}
                                />
                                <TextInput
                                    className={indexSearchInputCn}
                                    name="squareTo"
                                    onUpdate={setSquareTo}
                                    placeholder={i18n('squareTo')}
                                    size="l"
                                    controlProps={{
                                        min: 0,
                                    }}
                                    type="number"
                                    value={String(filters.squareTo)}
                                />
                            </div>
                            <div className={indexSearchMiniInputCn}>
                                <TextInput
                                    className={indexSearchInputCn}
                                    name="priceFrom"
                                    onUpdate={setPriceFrom}
                                    placeholder={i18n('priceFrom')}
                                    size="l"
                                    controlProps={{
                                        min: 0,
                                    }}
                                    type="number"
                                    value={String(filters.priceFrom)}
                                />
                                <TextInput
                                    className={indexSearchInputCn}
                                    name="priceTo"
                                    onUpdate={setPriceTo}
                                    placeholder={i18n('priceTo')}
                                    size="l"
                                    controlProps={{
                                        min: 0,
                                    }}
                                    type="number"
                                    value={String(filters.priceTo)}
                                />
                            </div>
                            <Select
                                onUpdate={setBathroom}
                                value={[optionNames.bathroom[filters.bathroom || 0].value || '']}
                                className={indexSearchInputCn}
                                name="bathroom"
                                options={optionNames.bathroom}
                                width="max"
                                size="l"
                                id="select_bathroom"
                            />
                            <Switch
                                onUpdate={setHeating}
                                checked={filters.heating}
                                className={indexSearchSwitchCn}
                                name="heating"
                                content={i18n('heating')}
                                size="l"
                            />
                            <Switch
                                onUpdate={setOven}
                                checked={filters.oven}
                                className={indexSearchSwitchCn}
                                name="oven"
                                content={i18n('oven')}
                                size="l"
                            />
                            <Switch
                                onUpdate={setDishwasher}
                                checked={filters.dishwasher}
                                className={indexSearchSwitchCn}
                                name="dishwasher"
                                content={i18n('dishwasher')}
                                size="l"
                            />
                            <Switch
                                onUpdate={setBalcony}
                                checked={filters.balcony}
                                className={indexSearchSwitchCn}
                                name="balcony"
                                content={i18n('balcony')}
                                size="l"
                            />
                            <Switch
                                onUpdate={setAnimal}
                                checked={filters.animal}
                                className={indexSearchSwitchCn}
                                name="animal"
                                content={i18n('animal')}
                                size="l"
                            />
                            <Switch
                                onUpdate={setConditioner}
                                checked={filters.conditioner}
                                className={indexSearchSwitchCn}
                                name="conditioner"
                                content={i18n('conditioner')}
                                size="l"
                            />
                            <Switch
                                onUpdate={setMicrowave}
                                checked={filters.microwave}
                                className={indexSearchSwitchCn}
                                name="microwave"
                                content={i18n('microwave')}
                                size="l"
                            />
                            <div className={indexSearchButtonContainer}>
                                {params.size > 0 && (
                                    <Button
                                        size="l"
                                        view="flat-action"
                                        width="auto"
                                        onClick={share}
                                        loading={shareProgress}
                                    >
                                        {i18nIndex('share')}
                                    </Button>
                                )}
                                <Button
                                    size="l"
                                    width="max"
                                    type="submit"
                                    view="action"
                                >
                                    {i18nIndex('search')}
                                </Button>
                                <Button
                                    size="l"
                                    width="auto"
                                    type="reset"
                                    view="flat-action"
                                    onClick={handleResetFilters}
                                >
                                    {i18nIndex('resetForm')}
                                </Button>
                            </div>
                        </form>
                    </div>
                </Disclosure>
            </Card>
            {publications && view === 'map' && (
                <Card>
                    <Map
                        className={indexMapCn}
                        center={mapCenter}
                        zoom={11}
                    >
                        <MarkerClusterGroup maxClusterRadius={55}>
                            {publications?.map(publication => (
                                <Marker
                                    key={publication.id}
                                    position={{ lat: publication.lat, lng: publication.lon }}
                                    icon={LeafletBlueMarker}
                                    title={`${publication.street}, ${publication.houseNumber}`}
                                >
                                    <Popup>
                                        <Link to={`/publication/${publication.id}`}>
                                            <PublicationMini publication={publication} />
                                        </Link>
                                    </Popup>
                                </Marker>
                            ))}
                        </MarkerClusterGroup>
                    </Map>
                </Card>
            )}
            {(!publications?.length && !loading) && (
                <EmptyContent>{i18nIndex('empty')}</EmptyContent>
            )}
            {view === 'list' && publications?.map(item => (
                <Link key={item.id} to={`/publication/${item.id}`} className={indexLinkCn}>
                    <Publication
                        className={publicationCn}
                        key={item.id}
                        publication={item}
                    />
                </Link>
            ))}

            {!loading && !isAllLoaded && <VisibilityObserver onVisible={loadNext} />}

            {loading && (
                <>
                    <PublicationSkeleton className={publicationCn} />
                    <PublicationSkeleton className={publicationCn} />
                    <PublicationSkeleton className={publicationCn} />
                    <PublicationSkeleton className={publicationCn} />
                    <PublicationSkeleton className={publicationCn} />
                </>
            )}
        </LayoutPage>
    );
});

export default Index;
